// The credential endpoints (crates/ocs-api/src/adapters/http/credentials): reads are
// metadata only; create, update, replace-value and delete are device-signed, with the value sealed
// here first. The paths signed are the paths requested, so they are spelled once.
import { queryOptions } from "@tanstack/react-query";

import { api, type Schemas } from "../api/client";
import { ApiError, unwrap } from "../api/queries";
import type { UpdateCredential } from "./edit";
import { type Signer, sealText, signedBody } from "./requests";
import { type Credential, createPayload, fieldNames, type SecretDraft, type ValueShape, plaintext } from "./templates";

export type CredentialUsage = Schemas["CredentialUsageDto"];

const orgPath = (orgId: string) => `/api/orgs/${orgId}/credentials`;
const credentialPath = (id: string) => `/api/credentials/${id}`;

export const credentialsQuery = (orgId: string | undefined) =>
  queryOptions({
    queryKey: ["credentials", orgId],
    queryFn: () =>
      unwrap(api.GET("/api/orgs/{org_id}/credentials", { params: { path: { org_id: orgId ?? "" } } })),
    enabled: orgId !== undefined,
  });

export const credentialUsageQuery = (credentialId: string) =>
  queryOptions({
    queryKey: ["credential-usage", credentialId],
    queryFn: () =>
      unwrap(api.GET("/api/credentials/{credential_id}/usage", { params: { path: { credential_id: credentialId } } })),
  });

/** Seal the value, sign the create request, send it. */
export async function createSecret(
  signer: Signer,
  orgId: string,
  draft: SecretDraft,
  read: (name: string) => string,
): Promise<Credential> {
  const t = draft.template;
  const sealed = await sealText(signer.proto, orgId, t.kind, plaintext(t.value, read));
  const body = await signedBody(signer, "POST", orgPath(orgId), createPayload(draft, sealed));
  return unwrap(api.POST("/api/orgs/{org_id}/credentials", { params: { path: { org_id: orgId } }, body }));
}

/** Rename, rescope or change the settings of a secret; its value is untouched. */
export async function updateSecret(signer: Signer, credentialId: string, payload: UpdateCredential): Promise<Credential> {
  const body = await signedBody(signer, "PUT", credentialPath(credentialId), payload);
  return unwrap(api.PUT("/api/credentials/{credential_id}", { params: { path: { credential_id: credentialId } }, body }));
}

/** Rotate: a new value under the same fields, sealed for the credential's org and kind. */
export async function replaceSecretValue(
  signer: Signer,
  credential: Credential,
  shape: ValueShape,
  read: (name: string) => string,
): Promise<Credential> {
  const sealed = await sealText(signer.proto, credential.org_id, credential.kind, plaintext(shape, read));
  const path = `${credentialPath(credential.id)}/value`;
  // Named fields go with the value, so a secret can change shape (a legacy Jira token
  // becoming site + email + token); a single value keeps the stored fields.
  const payload = shape.kind === "named" ? { sealed_value: sealed, fields: fieldNames(shape) } : { sealed_value: sealed };
  const body = await signedBody(signer, "PUT", path, payload);
  return unwrap(
    api.PUT("/api/credentials/{credential_id}/value", { params: { path: { credential_id: credential.id } }, body }),
  );
}

export async function deleteSecret(signer: Signer, credentialId: string): Promise<void> {
  const body = await signedBody(signer, "DELETE", credentialPath(credentialId), {});
  let response: Response;
  let error: unknown;
  try {
    ({ response, error } = await api.DELETE("/api/credentials/{credential_id}", {
      params: { path: { credential_id: credentialId } },
      body,
    }));
  } catch (e) {
    throw new ApiError(0, e instanceof Error ? e.message : e);
  }
  if (!response.ok) throw new ApiError(response.status, error);
}
